import React from "react";
import {
  Document,
  Page,
  Text,
  View,
  StyleSheet,
  Image,
  Font,
} from "@react-pdf/renderer";
import moment from "moment";

// Font for consistent output
Font.register({
  family: "Helvetica",
  fonts: [{ src: "https://fonts.gstatic.com/s/helvetica.ttf" }],
});

const styles = StyleSheet.create({
  page: {
    fontFamily: "Helvetica",
    fontSize: 10,
    padding: 35,
    backgroundColor: "#fff",
  },
  header: {
    marginBottom: 20,
    borderBottom: "1pt solid #ccc",
    paddingBottom: 8,
  },
  logo: { height: 50, width: 120, marginBottom: 10 },
  title: { fontSize: 14, fontWeight: "bold", marginBottom: 2,textTransform:'uppercase' },
  text: { marginBottom: 2 },
  table: {
    display: "table",
    width: "100%",
    borderStyle: "solid",
    borderWidth: 1,
    borderColor: "#ccc",
    borderRightWidth: 0,
    borderBottomWidth: 0,
    marginTop: 10,
  },
  tableRow: { flexDirection: "row" },
  tableHeader: {
    backgroundColor: "#db6f2f",
    fontWeight: "bold",
  },
  cellBase: {
    borderStyle: "solid",
    borderColor: "#ccc",
    borderWidth: 1,
    borderLeftWidth: 0,
    borderTopWidth: 0,
    padding: 5,
    textAlign: "center",
  },
  cellDate: {
    width: "25%",
    textAlign: "left",
    paddingLeft: 8,
  },
  cellSmall: {
    width: "15%",
  },
  summary: {
    marginTop: 20,
    borderTop: "1pt solid #ccc",
    paddingTop: 8,
  },
  signatureContainer: {
    flexDirection: "row",
    justifyContent: "space-between",
    marginTop: 40,
    fontSize: 10,
  },
  line: {
    width: "45%",
  },
});

// Helper function to chunk records into pages of size N
const chunkArray = (array, size) => {
  const result = [];
  for (let i = 0; i < array.length; i += size) {
    result.push(array.slice(i, i + size));
  }
  return result;
};

const PayrollPDFCustom = ({ user, records, summary, startDate, endDate }) => {
  // Split records into chunks (20 per page)
  const pages = chunkArray(records, 20);

  return (
    <Document>
      {pages.map((pageRecords, pageIndex) => (
        <Page size="A4" style={styles.page} key={pageIndex}>
          {/* Header (always visible on each page) */}
          <View style={styles.header}>
            {pageIndex === 0 && (
              <>
                <Image src="/assets/images/logo.png" style={styles.logo} />
                <Text style={styles.title}>{user.name}</Text>
                <Text style={styles.text}>
                   Rate: £
                  {user.rate}{" "}
                  {user.payroll_type === "hourly"
                    ? "/ hr"
                    : user.payroll_type === "daily"
                    ? "/ day"
                    : "/ month"}
                </Text>
                <Text style={styles.text}>
                  Period: {moment(startDate).format("DD MMM")} –{" "}
                  {moment(endDate).format("DD MMM YYYY")}
                </Text>
              </>
            )}
          </View>

          {/* Table */}
          <View style={styles.table}>
            {/* Thead */}
            <View style={[styles.tableRow, styles.tableHeader]}>
              <Text style={[styles.cellBase, styles.cellDate, { color: "white" }]}>
                Day & Date
              </Text>
              <Text style={[styles.cellBase, styles.cellSmall, { color: "white" }]}>
                Punch In
              </Text>
              <Text style={[styles.cellBase, styles.cellSmall, { color: "white" }]}>
                Punch Out
              </Text>
              <Text style={[styles.cellBase, styles.cellSmall, { color: "white" }]}>
                Work Hours
              </Text>
              <Text style={[styles.cellBase, styles.cellSmall, { color: "white" }]}>
                Approved
              </Text>
              <Text style={[styles.cellBase, styles.cellSmall, { color: "white" }]}>
                Status
              </Text>
            </View>

            {/* Rows */}
            {pageRecords.map((a, i) => (
              <View style={styles.tableRow} key={i}>
                <Text style={[styles.cellBase, styles.cellDate]}>
                  {moment(a.attendance_date || a.date).format("ddd, DD MMM")}
                </Text>
                <Text style={[styles.cellBase, styles.cellSmall]}>
                  {a.punch_in_time
                    ? moment(a.punch_in_time, "HH:mm:ss").format("hh:mm A")
                    : "--"}
                </Text>
                <Text style={[styles.cellBase, styles.cellSmall]}>
                  {a.punch_out_time
                    ? moment(a.punch_out_time, "HH:mm:ss").format("hh:mm A")
                    : "--"}
                </Text>
                <Text style={[styles.cellBase, styles.cellSmall]}>
                  {a.total_work_hours || 0}
                </Text>
                <Text style={[styles.cellBase, styles.cellSmall]}>
                  {a.approved_hours || 0}
                </Text>
                <Text style={[styles.cellBase, styles.cellSmall]}>
                {a.status === "on-time" || a.status === "early"
                  ? "PRESENT"
                  : String(a.status || "N/A").toUpperCase()}
              </Text>
              </View>
            ))}
          </View>

          {/* Summary & Signature — only on last page */}
          {pageIndex === pages.length - 1 && (
            <>
              <View style={styles.summary}>
                <Text>Total Work Hours: {summary.total_hours}</Text>
                <Text>Total Overtime: {summary.total_overtime}</Text>
                <Text>Approved Hours: {summary.approved_hours}</Text>
                <Text>Wage: £{summary.wage}</Text>
              </View>

              <View style={styles.signatureContainer}>
                <View style={styles.line}>
                  <View
                    style={{
                      borderTopWidth: 1,
                      borderTopColor: "black",
                      width: 150,
                      marginBottom: 10,
                    }}
                  />
                  <Text>Signature of Employee</Text>
                </View>
                <View style={styles.line}>
                  <View
                    style={{
                      borderTopWidth: 1,
                      borderTopColor: "black",
                      width: 150,
                      marginBottom: 10,
                    }}
                  />
                  <Text>Administration Signature</Text>
                </View>
              </View>
            </>
          )}
        </Page>
      ))}
    </Document>
  );
};

export default PayrollPDFCustom;
